import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { Dialog } from "@headlessui/react"
import { X, Trash2 } from "lucide-react"
import BrandAutocomplete from "../../brand-autocomplete"
import Select from "../../form/select"
import ReactJSONEditor from "../../react-json-editor"
import {
  useFetchRateCardsConfigsQuery,
  useFetchRateCardsConfigQuery,
  useFetchDefaultRateCardsConfigQuery,
  useUpdateOrCreateRateCardsConfigMutation,
  useDeleteRateCardsConfigMutation
} from "../../../../redux/queries"
import { useToast } from "../../../hooks"
import { Brand, RateCardsConfigDetails } from "../../../../types"

interface Props {
  brands: Brand[]
}

const emptyDetails: Record<string, unknown> = {}

const ConfigRateCards: FC<Props> = ({ brands }) => {
  const { pushSuccessToast, pushErrorToast } = useToast()

  const [selectedBrandId, setSelectedBrandId] = useState<string>("")
  const [selectedConfigId, setSelectedConfigId] = useState<string>("")
  const [isAddingNew, setIsAddingNew] = useState(false)
  const [configName, setConfigName] = useState("")
  const [isDefault, setIsDefault] = useState(false)
  const [details, setDetails] = useState<Record<string, unknown>>(emptyDetails)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const { data: configs, isFetching: isFetchingConfigs } =
    useFetchRateCardsConfigsQuery(
      { brand_id: selectedBrandId },
      { skip: !selectedBrandId }
    )

  const { data: configDetails, isFetching: isFetchingDetails } =
    useFetchRateCardsConfigQuery(
      { id: selectedConfigId },
      { skip: !selectedConfigId || isAddingNew }
    )

  const { data: defaultConfig } = useFetchDefaultRateCardsConfigQuery({})

  const [updateOrCreate, { isLoading: isSaving }] =
    useUpdateOrCreateRateCardsConfigMutation()
  const [deleteConfig, { isLoading: isDeleting }] =
    useDeleteRateCardsConfigMutation()

  const configOptions = useMemo(() => {
    const list = Array.isArray(configs) ? configs : []
    return list.map(c => ({
      id: c.id,
      name: c.name + (c.is_default === "1" ? " (default)" : "")
    }))
  }, [configs])

  const selectedConfig = useMemo(
    () => configs?.find(c => c.id === selectedConfigId),
    [configs, selectedConfigId]
  )

  // Sync local state when config details load
  useEffect(() => {
    if (configDetails && !isAddingNew) {
      setConfigName(configDetails.name ?? "")
      setIsDefault(configDetails.is_default === "1")
      setDetails(configDetails.details ?? emptyDetails)
    }
  }, [configDetails, isAddingNew])

  // Reset form when starting new - pre-populate with default config
  useEffect(() => {
    if (isAddingNew) {
      setConfigName("")
      setIsDefault(false)
      // Default config API returns { details: {...} } - extract the details
      const defaultDetails =
        (defaultConfig as { details?: Record<string, unknown> })?.details ??
        emptyDetails
      setDetails(defaultDetails)
    }
  }, [isAddingNew, defaultConfig])

  const handleBrandChange = useCallback((brandId: string) => {
    setSelectedBrandId(brandId)
    setSelectedConfigId("")
    setIsAddingNew(false)
  }, [])

  const handleConfigChange = useCallback((configId: string) => {
    setSelectedConfigId(configId)
    setIsAddingNew(false)
  }, [])

  const handleClear = useCallback(() => {
    setSelectedBrandId("")
    setSelectedConfigId("")
    setIsAddingNew(false)
  }, [])

  const handleAddNew = useCallback(() => {
    setSelectedConfigId("")
    setIsAddingNew(true)
  }, [])

  const handleCancelAdd = useCallback(() => {
    setIsAddingNew(false)
  }, [])

  const handleSave = useCallback(async () => {
    if (!configName.trim()) {
      pushErrorToast("Config name is required")
      return
    }

    try {
      const payload: Partial<RateCardsConfigDetails> = {
        name: configName,
        brand_id: Number(selectedBrandId),
        is_default: isDefault ? "1" : "0",
        details
      }

      if (!isAddingNew && selectedConfigId) {
        payload.id = selectedConfigId
      }

      await updateOrCreate(payload).unwrap()
      pushSuccessToast(isAddingNew ? "Config created" : "Config updated")

      if (isAddingNew) {
        setIsAddingNew(false)
      }
    } catch {
      pushErrorToast("Failed to save config")
    }
  }, [
    configName,
    selectedBrandId,
    isDefault,
    details,
    isAddingNew,
    selectedConfigId,
    updateOrCreate,
    pushSuccessToast,
    pushErrorToast
  ])

  const handleDelete = useCallback(async () => {
    if (!selectedConfigId) return

    try {
      await deleteConfig(selectedConfigId).unwrap()
      pushSuccessToast("Config deleted")
      setShowDeleteConfirm(false)
      setSelectedConfigId("")
    } catch {
      pushErrorToast("Failed to delete config")
    }
  }, [selectedConfigId, deleteConfig, pushSuccessToast, pushErrorToast])

  const isEditing = isAddingNew || !!selectedConfigId
  const isLoading = isFetchingConfigs || isFetchingDetails

  return (
    <div>
      {/* Brand and Config selectors */}
      <div className="flex items-end gap-3 mb-6">
        <BrandAutocomplete
          selected={selectedBrandId}
          brands={brands}
          hideLabel
          onChange={handleBrandChange}
        />

        {selectedBrandId && !isAddingNew && configOptions.length > 0 && (
          <div className="w-64">
            <Select
              label="Config"
              data={configOptions}
              selected={
                selectedConfig
                  ? { id: selectedConfig.id, name: selectedConfig.name }
                  : null
              }
              setSelected={handleConfigChange}
              isLoading={isFetchingConfigs}
            />
          </div>
        )}

        {selectedBrandId &&
          !isAddingNew &&
          !isFetchingConfigs &&
          configOptions.length === 0 && (
            <span className="text-sm text-gray-500 mb-px">
              No configs found
            </span>
          )}

        {selectedBrandId && !isAddingNew && (
          <button
            type="button"
            onClick={handleAddNew}
            className="mb-px px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            + New
          </button>
        )}

        {isAddingNew && (
          <button
            type="button"
            onClick={handleCancelAdd}
            className="mb-px p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Cancel"
          >
            <X size={18} />
          </button>
        )}

        {selectedBrandId && !isAddingNew && (
          <button
            type="button"
            onClick={handleClear}
            className="mb-px p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Clear selection"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {isLoading && <div className="text-sm text-gray-500">Loading...</div>}

      {/* Edit form */}
      {isEditing && !isLoading && (
        <div className="space-y-6">
          {/* Config name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Config Name
            </label>
            <input
              type="text"
              value={configName}
              onChange={e => setConfigName(e.target.value)}
              placeholder="Enter config name"
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Default checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is-default"
              checked={isDefault}
              onChange={e => setIsDefault(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="is-default" className="text-sm text-gray-700">
              Default config for this brand
            </label>
          </div>

          {/* JSON Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Details
            </label>
            <ReactJSONEditor
              name="details"
              json={details}
              minHeight={400}
              onChangeJSON={val => setDetails(val as Record<string, unknown>)}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>

            {!isAddingNew && selectedConfigId && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-bold text-gray-900 mb-2">
              Delete Config
            </Dialog.Title>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this rate cards configuration?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  )
}

export default ConfigRateCards
